import React from 'react';
import { Application } from '~/data/useApplication';
import { SchoolCustomizations } from '~/data/useSchoolCustomizations';

export type Process =
  | 'decline'
  | 'issuePreOffer'
  | 'passScreening'
  | 'additionalDocumentRequest'
  | 'issueFinalOffer'
  | 'waitlisted'
  | 'deferralRequest'
  | 'dismissDocumentRequest'
  | 'confirmEnrolment';

type State = {
  application: Application;
  startProcess: (process: Process) => void;
  completeProcess: () => void;
  activeProcess: Process | undefined;
  nextProcess: Process | undefined;
  setNextProcess: (action: Process) => void;
  schoolCustomizations: SchoolCustomizations;
};
const context = React.createContext<State | undefined>(undefined);

type AppProcessingProviderProps = {
  application: Application;
  children: React.ReactNode;
  schoolCustomizations: SchoolCustomizations;
};
export function AppProcessingProvider(props: AppProcessingProviderProps) {
  const [activeProcess, setActiveProcess] = React.useState<Process | undefined>(undefined);
  const [nextProcess, setNextProcess] = React.useState<Process | undefined>(undefined);

  const value = React.useMemo<State>(() => {
    return {
      application: props.application,
      activeProcess,
      startProcess: (process: Process) => setActiveProcess(process),
      completeProcess: () => {
        setNextProcess(undefined);
        setActiveProcess(undefined);
      },
      nextProcess,
      setNextProcess: (action: Process) => setNextProcess(action),
      schoolCustomizations: props.schoolCustomizations,
    };
  }, [activeProcess, props.application, nextProcess, props.schoolCustomizations]);

  return <context.Provider value={value}>{props.children}</context.Provider>;
}
export function useAppProcessing() {
  const state = React.useContext(context);
  if (!state) throw new Error('useAppProcessing should be used within <useAppProcessingProvider />');
  return state;
}
